import React, {useContext, useDeferredValue, useEffect, useMemo, useState} from "react";
import {ActivityIndicator, Modal, StyleSheet, Text, TextInput, TouchableOpacity, View} from "react-native";
import {MaterialCommunityIcons} from '@expo/vector-icons';
import {OrdersContext} from "../helpers/OrdersContext";
import {SystemContext} from "../helpers/SystemContext";
import {priceFormatter} from "../helpers";
import {useTranslation} from "react-i18next";
import HappyAlert from './HappyAlert';

import { ModalKeyboardAvoider, ModalScrollBody } from './KeyboardAware';
import {searchFoodByKeywords} from '../helpers/foodKeywordSearch';

const ORANGE = '#F97316';
// Keyword search lives in a pure helper (faster, unit-tested); re-exported here
// because SaleScreen and printerProfiles import it from this module.
export {searchFoodByKeywords};

const PairingModal = ({ visible, onClose, pairingItem, orderId, order, onPairingSaved }) => {
    const { t } = useTranslation();

    const {pairOrderItem} = useContext(OrdersContext);
    const {foods} = useContext(SystemContext);
    const [searchValue, setSearchValue] = useState("");
    const [defaultSearchTerms, setDefaultSearchTerms] = useState("");
    // Key of the candidate row currently being paired (spinner + lock while the PUT is in-flight).
    const [savingKey, setSavingKey] = useState(null);

    const deferredSearchValue = useDeferredValue(searchValue);
    const deferredDefaultSearchTerms = useDeferredValue(defaultSearchTerms);

    const pairingCandidates = useMemo(() => {
        if (!Array.isArray(foods) || foods.length === 0) {
            return [];
        }

        const searchTerm = (deferredSearchValue || deferredDefaultSearchTerms || pairingItem?.remoteName || '').trim();
        if (!searchTerm) {
            return [];
        }

        const searchableFoods = foods.filter((food) => (
            food?.foodType !== 'ingredient' && food?.visible === true && food?.enabled === true
        ));

        return searchFoodByKeywords(searchTerm, searchableFoods)
            .flatMap((food) => {
                const sizes = Array.isArray(food?.sizes) ? food.sizes : [];
                return sizes.map((size) => ({
                    food,
                    size,
                    key: `${food._id || food?.name?.hu || 'food'}-${size._id || size?.amount || 'size'}`,
                }));
            })
            .slice(0, 200);
    }, [deferredDefaultSearchTerms, deferredSearchValue, foods, pairingItem?.remoteName]);

    useEffect(() => {
        setSearchValue("");
        setSavingKey(null);
        setDefaultSearchTerms("");

        // Set default search terms from pairingItem
        if (pairingItem?.name) {
            setDefaultSearchTerms(pairingItem?.name.hu || pairingItem?.name.en);
        } else if(pairingItem?.remoteName) {
            setDefaultSearchTerms(pairingItem?.remoteName);
        }
    }, [visible, pairingItem]);


    /*
    useEffect(() => {
        // Use the improved search function
        const searchTerm = searchValue?.length > 0 ? searchValue : defaultSearchTerms;

        if (!searchTerm || !foods) {
            setFilteredFoods([]);
            return;
        }

        // Get sorted food results using the searchFoodByKeywords function
        const searchResults = searchFoodByKeywords(searchTerm, foods);

        // Expand each food with its sizes
        const expandedResults = searchResults.flatMap(food =>
            food.sizes.map(size => ({ ...food, size }))
        );
        setFilteredFoods(expandedResults);
    }, [searchValue, foods, defaultSearchTerms]);
    */



    /**
     * Pair the incoming online-order item with the tapped local product+size and
     * close the modal IMMEDIATELY. A single tap on a result row is enough — no
     * separate "select then save" step, and no waiting on the network: the
     * pairing is applied locally (both to the parent screen's order and to the
     * OrdersContext so it survives the periodic poll) and persisted to the
     * backend in the background.
     *
     * This is the whole point of the flow — the cashier keeps working instantly
     * instead of watching a spinner during the PUT round-trip. If the background
     * save fails, {@link OrdersContext.pairOrderItem} reverts the optimistic
     * pairing and raises a premium HappyAlert so it can be retried.
     *
     * @param {object} food the local product document
     * @param {object} size the chosen size variant of that product
     * @param {string} key  stable row key (guards against a double-tap)
     * @returns {void}
     */
    const handlePair = (food, size, key) => {
        if (!food || !size || savingKey) return;

        // Guard the URL before firing — a missing remoteCode / size id would
        // build a `.../undefined/undefined` request that always 500s.
        if (!pairingItem?.remoteCode || !size?._id) {
            HappyAlert.error(t('pairing_save_failed_title'), t('pairing_save_failed_message'));

            return;
        }

        const optimisticPairedFood = {
            ...food,
            _id: size?._id || food?._id,
            foodId: food?._id,
            price: size?.price ?? food?.price,
            unit: size?.unit ?? food?.unit,
            amount: size?.amount ?? food?.amount,
            size,
            selectedSize: size,
            remoteName: food?.name?.hu || food?.name?.en || pairingItem?.remoteName,
        };

        // Guard against a double-tap in the frame before the modal unmounts.
        setSavingKey(key);

        // Flip the parent screen's row to "paired" instantly...
        onPairingSaved?.(pairingItem, optimisticPairedFood);

        // ...and hand persistence to the OrdersContext, which keeps the pairing
        // applied across polls and saves it to the backend in the background
        // (reverting + alerting only if that save fails). Fire-and-forget: we do
        // NOT await it, so the modal closes with zero network latency.
        pairOrderItem?.({
            order,
            pairingItem,
            pairedFood: optimisticPairedFood,
            sizeId: size._id,
        });

        onClose();
    };

    const hasSearch = (searchValue || defaultSearchTerms || pairingItem?.remoteName || '').trim().length > 0;

    return (
        <Modal visible={visible} transparent animationType="slide" hardwareAccelerated onRequestClose={onClose}>
            <ModalKeyboardAvoider style={styles.backdrop} justify="center">
                <View style={styles.card}>
                    {/* Header — icon chip + title/subtitle + close */}
                    <View style={styles.header}>
                        <View style={styles.headerIcon}>
                            <MaterialCommunityIcons name="link-variant" size={24} color={ORANGE} />
                        </View>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.title} numberOfLines={1}>{t('pairing_modal_title')}</Text>
                            <Text style={styles.subtitle} numberOfLines={1}>{t('pairing_modal_subtitle')}</Text>
                        </View>
                        <TouchableOpacity style={styles.closeButton} onPress={onClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                            <MaterialCommunityIcons name="close" size={22} color="#94a3b8" />
                        </TouchableOpacity>
                    </View>

                    {/* Source item — the incoming online-order line we're pairing FROM */}
                    <View style={styles.sourceCard}>
                        <View style={{ flex: 1 }}>
                            {order?.orderFrom ? (
                                <View style={styles.platformTag}>
                                    <MaterialCommunityIcons name="storefront-outline" size={12} color={ORANGE} />
                                    <Text style={styles.platformTagText} numberOfLines={1}>{String(order.orderFrom).toUpperCase()}</Text>
                                </View>
                            ) : null}
                            <Text style={styles.sourceName} numberOfLines={2}>{pairingItem?.remoteName}</Text>
                        </View>
                        <Text style={styles.sourcePrice}>{priceFormatter(pairingItem?.remotePrice)}</Text>
                    </View>

                    {/* Search */}
                    <View style={styles.searchWrap}>
                        <MaterialCommunityIcons name="magnify" size={20} color="#94a3b8" style={{ marginRight: 8 }} />
                        <TextInput
                            style={styles.searchInput}
                            placeholder={t("search_placeholder")}
                            placeholderTextColor="#94a3b8"
                            value={searchValue}
                            onChangeText={setSearchValue}
                        />
                        {searchValue.length > 0 ? (
                            <TouchableOpacity onPress={() => setSearchValue("")} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                                <MaterialCommunityIcons name="close-circle" size={18} color="#cbd5e1" />
                            </TouchableOpacity>
                        ) : null}
                    </View>

                    {/* Results */}
                    <ModalScrollBody style={styles.foodList} contentContainerStyle={styles.foodListContent}>
                        {pairingCandidates.length === 0 ? (
                            <View style={styles.emptyWrap}>
                                <View style={styles.emptyIconCircle}>
                                    <MaterialCommunityIcons
                                        name={hasSearch ? "food-off-outline" : "magnify"}
                                        size={30}
                                        color="#cbd5e1"
                                    />
                                </View>
                                <Text style={styles.emptyText}>
                                    {hasSearch ? t('no_matching_foods') : t('pairing_search_prompt')}
                                </Text>
                            </View>
                        ) : (
                            pairingCandidates.map(({ food, size, key }) => {
                                const isRowSaving = savingKey === key;
                                const isLocked = savingKey && !isRowSaving;

                                return (
                                    <TouchableOpacity
                                        key={key}
                                        activeOpacity={0.85}
                                        disabled={!!savingKey}
                                        onPress={() => handlePair(food, size, key)}
                                        style={[
                                            styles.foodRow,
                                            isRowSaving && styles.foodRowSaving,
                                            isLocked && styles.foodRowLocked,
                                        ]}
                                    >
                                        <View style={styles.foodInfo}>
                                            <Text style={styles.foodName} numberOfLines={2}>
                                                {food.name["hu"] || food.name["en"]}
                                            </Text>
                                            <View style={styles.metaRow}>
                                                <View style={styles.sizeChip}>
                                                    <Text style={styles.sizeChipText}>{size.amount}{size.unit}</Text>
                                                </View>
                                                <Text style={styles.foodPrice}>{priceFormatter(size.price)}</Text>
                                            </View>
                                        </View>
                                        <View style={[styles.pairIcon, isRowSaving && styles.pairIconSaving]}>
                                            {isRowSaving ? (
                                                <ActivityIndicator size="small" color="#fff" />
                                            ) : (
                                                <MaterialCommunityIcons name="plus" size={22} color={ORANGE} />
                                            )}
                                        </View>
                                    </TouchableOpacity>
                                );
                            })
                        )}
                    </ModalScrollBody>

                    {/* Footer — hint + close */}
                    <View style={styles.footer}>
                        <View style={styles.hintRow}>
                            <MaterialCommunityIcons name="gesture-tap" size={16} color="#94a3b8" />
                            <Text style={styles.hintText}>{t('pairing_tap_to_pair')}</Text>
                        </View>
                        <TouchableOpacity style={styles.closeAction} onPress={onClose} disabled={!!savingKey}>
                            <Text style={styles.closeActionText}>{t('cancel')}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </ModalKeyboardAvoider>
        </Modal>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        alignItems: 'center',
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        paddingHorizontal: 16,
    },
    card: {
        width: '100%',
        maxWidth: 520,
        maxHeight: '90%',
        backgroundColor: '#fff',
        borderRadius: 24,
        overflow: 'hidden',
        shadowColor: '#0f172a',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.2,
        shadowRadius: 24,
        elevation: 12,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#f1f5f9',
    },
    headerIcon: {
        width: 46,
        height: 46,
        borderRadius: 14,
        backgroundColor: '#fff2e8',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
    },
    title: {
        fontSize: 18,
        fontWeight: '800',
        color: '#0f172a',
    },
    subtitle: {
        fontSize: 12.5,
        color: '#64748b',
        marginTop: 2,
    },
    closeButton: {
        padding: 6,
        marginLeft: 8,
    },
    sourceCard: {
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: 20,
        marginTop: 16,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#fde8d6',
        backgroundColor: '#fffaf5',
    },
    platformTag: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: 8,
        backgroundColor: '#fff2e8',
        marginBottom: 4,
    },
    platformTagText: {
        marginLeft: 4,
        fontSize: 10,
        fontWeight: '800',
        letterSpacing: 0.5,
        color: '#9a3412',
    },
    sourceName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#0f172a',
    },
    sourcePrice: {
        fontSize: 16,
        fontWeight: '800',
        color: ORANGE,
        marginLeft: 12,
    },
    searchWrap: {
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: 20,
        marginTop: 14,
        paddingHorizontal: 14,
        height: 50,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#e2e8f0',
        backgroundColor: '#f8fafc',
    },
    searchInput: {
        flex: 1,
        fontSize: 15,
        color: '#0f172a',
        paddingVertical: 0,
    },
    foodList: {
        marginTop: 12,
        paddingHorizontal: 20,
    },
    foodListContent: {
        paddingBottom: 12,
    },
    foodRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        paddingHorizontal: 14,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#eef2f7',
        backgroundColor: '#fff',
        marginBottom: 10,
    },
    foodRowSaving: {
        borderColor: ORANGE,
        backgroundColor: '#fffaf5',
    },
    foodRowLocked: {
        opacity: 0.5,
    },
    foodInfo: {
        flex: 1,
        marginRight: 12,
    },
    foodName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#0f172a',
    },
    metaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 6,
    },
    sizeChip: {
        paddingVertical: 2,
        paddingHorizontal: 8,
        borderRadius: 8,
        backgroundColor: '#f1f5f9',
        marginRight: 10,
    },
    sizeChipText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#475569',
    },
    foodPrice: {
        fontSize: 14.5,
        fontWeight: '700',
        color: '#64748b',
    },
    pairIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff2e8',
    },
    pairIconSaving: {
        backgroundColor: ORANGE,
    },
    emptyWrap: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 44,
    },
    emptyIconCircle: {
        width: 72,
        height: 72,
        borderRadius: 36,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        marginBottom: 14,
    },
    emptyText: {
        fontSize: 14,
        color: '#94a3b8',
        fontWeight: '600',
        textAlign: 'center',
        paddingHorizontal: 24,
    },
    footer: {
        borderTopWidth: 1,
        borderTopColor: '#f1f5f9',
        paddingHorizontal: 20,
        paddingTop: 14,
        paddingBottom: 16,
    },
    hintRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
    },
    hintText: {
        marginLeft: 6,
        fontSize: 12.5,
        color: '#94a3b8',
        fontWeight: '600',
    },
    closeAction: {
        height: 52,
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
    },
    closeActionText: {
        color: '#475569',
        fontSize: 16,
        fontWeight: '700',
    },
});

export default PairingModal;
